import React, { useState } from 'react';
import { Send, Loader2, Phone, Mail as MailIcon } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast({
        title: "Thank You for Contacting Us! 🎉",
        description: "We've received your message and will respond shortly to your email.",
        duration: 5000
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to send message. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '16px 20px',
    border: '1px solid var(--border-medium)',
    borderRadius: '0px',
    fontSize: '14px',
    fontFamily: 'Inter, -apple-system, sans-serif',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    transition: 'border-color 0.2s ease'
  };

  return (
    <section id="contact" className="section-padding" style={{ background: 'var(--bg-primary)' }}>
      <div className="luxury-container">
        <div className="grid-two-column">
          <div>
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>
              Get in Touch
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '40px' }}>
              Ready to experience professional tax preparation? Contact us today to get started.
            </p>
            
            <div style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                <Phone size={24} strokeWidth={1.5} color="var(--accent-gold)" />
                <div>
                  <p className="body-small" style={{ color: 'var(--text-meta)', marginBottom: '4px' }}>
                    Business Phone
                  </p>
                  <a href="tel:800-426-0441" className="body-large" style={{ color: 'var(--text-primary)', textDecoration: 'none' }}>
                    800-426-0441
                  </a>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <MailIcon size={24} strokeWidth={1.5} color="var(--accent-gold)" />
                <div>
                  <p className="body-small" style={{ color: 'var(--text-meta)', marginBottom: '4px' }}>
                    Email Address
                  </p>
                  <a href="mailto:taxprep@enlivenelite.tax" className="body-large" style={{ color: 'var(--text-primary)', textDecoration: 'none' }}>
                    taxprep@enlivenelite.tax
                  </a>
                </div>
              </div>
            </div>
            
            <a href="tel:800-426-0441" className="btn-primary" style={{ textDecoration: 'none' }}>
              <Phone size={16} />
              Call or Text Now
            </a>
          </div>
          
          <div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: 'var(--text-primary)' }}>
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                />
              </div>
              
              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: 'var(--text-primary)' }}>
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                />
              </div>
              
              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: 'var(--text-primary)' }}>
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                />
              </div>
              
              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: 'var(--text-primary)' }}>
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  rows="5"
                  style={{...inputStyle, resize: 'vertical'}}
                  onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                />
              </div>
              
              <button
                type="submit"
                className="btn-primary gold-accent"
                disabled={isSubmitting}
                style={{ width: '100%' }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;